import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Box,
  Typography,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Launch as LaunchIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { checkBrowserCompatibility, redirectToDefaultBrowser } from '../../utils/browserRedirect';

export const BrowserRedirectBanner: React.FC = () => {
  const [browserCheck, setBrowserCheck] = useState(checkBrowserCompatibility());
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Only show if browser is problematic and user hasn't dismissed
    const isDismissed = localStorage.getItem('browserRedirectDismissed') === 'true';
    if (browserCheck.shouldRedirect && !isDismissed) {
      setShowBanner(true);
    }
  }, [browserCheck.shouldRedirect]);

  const handleRedirect = () => {
    const success = redirectToDefaultBrowser();
    if (!success) {
      // Fallback: copy URL to clipboard
      handleCopyUrl();
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('browserRedirectDismissed', 'true');
  };

  if (!showBanner || dismissed) {
    return null;
  }

  return (
    <Collapse in={showBanner}>
      <Alert
        severity="info"
        sx={{
          borderRadius: 0,
          '& .MuiAlert-action': {
            alignItems: 'flex-start',
            paddingTop: 0
          }
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<LaunchIcon />}
              onClick={handleRedirect}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Open in Browser
            </Button>
            <Button
              size="small"
              startIcon={<CopyIcon />}
              onClick={handleCopyUrl}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <IconButton size="small" onClick={handleDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Typography variant="subtitle2" gutterBottom>
          Better Experience Available
        </Typography>
        <Typography variant="body2">
          You're viewing this in {browserCheck.browserName}'s browser. 
          For the best experience with secure authentication and all features, 
          open this page in your device's default browser.
        </Typography>
      </Alert>
    </Collapse>
  );
};